import React, { useEffect, useRef, useState } from 'react'
import { Gem } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import TelegramStarsPackages from '../components/TelegramStarsPackages'
import SbpPayment from '../components/SbpPayment'
import CryptoPayment from '../components/CryptoPayment'
import UzsPayment from '../components/UzsPayment'
import Uzs2Payment from '../components/Uzs2Payment'
import KztPayment from '../components/KztPayment'
import TjsPayment from '../components/TjsPayment'
import CardPayment from '../components/CardPayment'
import SkinsPayment from '../components/SkinsPayment'
import PromoBanner from '../components/PromoBanner'
import PromoCodeModal from '../components/PromoCodeModal'
import TermsAndInfo from '../../../components/TermsAndInfo'
import usePaymentStore from '../store/usePaymentStore'
import { PAYMENT_METHODS } from '../services/paymentApi'

/**
 * Payment Page
 * Главная страница пополнения баланса
 * Контейнер для выбора способа оплаты и отображения соответствующих пакетов
 */
const Payment = () => {
  const { t } = useTranslation()
  const { selectedMethod, availableMethods, isLoadingMethods, loadTopupData } = usePaymentStore()

  const amountTitleRef = useRef(null)
  const hasMountedRef = useRef(false)
  const [paymentMethodClickVersion, setPaymentMethodClickVersion] = useState(0)

  // Загрузка промо-данных при монтировании страницы
  useEffect(() => {
    loadTopupData()
  }, [loadTopupData])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }

    if (amountTitleRef.current) {
      requestAnimationFrame(() => {
        amountTitleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [paymentMethodClickVersion, selectedMethod])

  return (
    <div className="text-white lg:max-w-4xl lg:mx-auto">
      {/* Main Content */}
      <div className="p-6">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-full flex items-center justify-center">
            <Gem size={32} className="text-dark-primary" />
          </div>
          <h1 className="text-3xl font-bold text-accent-primary mb-2">
            {t('payment.pageTitle')}
          </h1>
        </div>

        {/* Промо-баннер и кнопка ввода промокода */}
        <PromoBanner />

        {/* Двухколоночный layout на десктопе: слева способы, справа пакеты */}
        <div className="lg:flex lg:gap-6 lg:items-start mt-10">
          {/* Левая колонка — выбор способа оплаты */}
          <div className="lg:w-80 lg:flex-shrink-0">
            <PaymentMethodSelector onMethodClick={() => setPaymentMethodClickVersion((previousValue) => previousValue + 1)} />
          </div>

          {/* Правая колонка — выбор пакета */}
          <div className="lg:flex-1 lg:min-w-0">
            {/* Package Selection Title */}
            <h2 ref={amountTitleRef} className="text-lg font-semibold text-white mt-8 mb-4 lg:mt-0">
              {selectedMethod !== PAYMENT_METHODS.SKINS && t('payment.selectAmount')}
            </h2>

            {/* Payment Method Content */}
            <div>
              {selectedMethod === PAYMENT_METHODS.TELEGRAM_STARS && (
                <TelegramStarsPackages />
              )}

              {selectedMethod === PAYMENT_METHODS.SKINS && (
                <SkinsPayment />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS && (
                <UzsPayment />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS2 && (
                <Uzs2Payment />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS3 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.UZS3) && (
                <UzsPayment method="uzs3" />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS4 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.UZS4) && (
                <UzsPayment method="uzs4" />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS5 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.UZS5) && (
                <UzsPayment method="uzs5" />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS6 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.UZS6) && (
                <UzsPayment method="uzs6" />
              )}

              {selectedMethod === PAYMENT_METHODS.UZS7 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.UZS7) && (
                <UzsPayment method="uzs7" />
              )}

              {selectedMethod === PAYMENT_METHODS.KZT1 && (
                <KztPayment />
              )}

              {selectedMethod === PAYMENT_METHODS.KZT2 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.KZT2) && (
                <TjsPayment key="kzt2" method="kzt2" />
              )}

              {selectedMethod === PAYMENT_METHODS.TJS1 && (
                <TjsPayment key="tjs1" />
              )}

              {selectedMethod === PAYMENT_METHODS.KGS1 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.KGS1) && (
                <TjsPayment key="kgs1" method="kgs1" />
              )}

              {selectedMethod === PAYMENT_METHODS.UAH1 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.UAH1) && (
                <TjsPayment key="uah1" method="uah1" />
              )}

              {selectedMethod === PAYMENT_METHODS.BYN1 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.BYN1) && (
                <TjsPayment key="byn1" method="byn1" />
              )}

              {selectedMethod === PAYMENT_METHODS.SBP && (
                <SbpPayment method={PAYMENT_METHODS.SBP} />
              )}

              {selectedMethod === PAYMENT_METHODS.SBP2 && (
                <SbpPayment method={PAYMENT_METHODS.SBP2} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARD && (
                <CardPayment method={PAYMENT_METHODS.CARD} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARDWW2 && (
                <CardPayment method={PAYMENT_METHODS.CARDWW2} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARDWW1 && (
                <CardPayment method={PAYMENT_METHODS.CARDWW1} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARDWW3 && (
                <CardPayment method={PAYMENT_METHODS.CARDWW3} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARDWW4 && (
                <CardPayment method={PAYMENT_METHODS.CARDWW4} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARDWW5 && (
                <CardPayment method={PAYMENT_METHODS.CARDWW5} />
              )}

              {selectedMethod === PAYMENT_METHODS.CARDWW6 && !isLoadingMethods && availableMethods.some((method) => method.id === PAYMENT_METHODS.CARDWW6) && (
                <CardPayment method={PAYMENT_METHODS.CARDWW6} />
              )}

              {selectedMethod === PAYMENT_METHODS.GOOGLE_PAY && (
                <CardPayment method={PAYMENT_METHODS.GOOGLE_PAY} />
              )}

              {selectedMethod === PAYMENT_METHODS.APPLE_PAY && (
                <CardPayment method={PAYMENT_METHODS.APPLE_PAY} />
              )}

              {selectedMethod === PAYMENT_METHODS.KZT_CARDWW4 && (
                <CardPayment method={PAYMENT_METHODS.KZT_CARDWW4} />
              )}

              {selectedMethod === PAYMENT_METHODS.CRYPTO && (
                <CryptoPayment />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Модальное окно ввода промокода */}
      <PromoCodeModal />

      {/* Terms of Service and Company Info */}
      <TermsAndInfo />
    </div>
  )
}

export default Payment
